//libs
import get from 'lodash/get';
import { getCachedVPDoc } from './helpers';
import { VP_BLOCK_MODE, VP_DOC_KEY, VP_STATE, VP_USER } from 'shared/components/VisitorPriortization/constants';
import Storage from 'shared/helpers/storage';

const getUserSegment = () => {
  try {
    const cachedUserInfo = Storage.getItem<string>(VP_USER) || '';
    if (cachedUserInfo === '') {
      return {
        key: 0,
        ruleID: '',
      };
    }
    return JSON.parse(cachedUserInfo);
  } catch (_) {
    return {
      key: 0,
      ruleID: '',
    };
  }
};

let visitorPriortizationConfig = __BROWSER__ ? getCachedVPDoc() : undefined;
const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

let onBlockedCallback = EMPTY_FUNC;
const vpDocSubscribers = new Set<(vpDoc?: VP.Doc) => void>();
let pathBlockMatchers: string[] = [];
let lastCachedVPDocRaw: string | null | undefined = undefined;

const setLocalVPDoc = (vpDoc?: VP.Doc) => {
  visitorPriortizationConfig = vpDoc;

  if (vpDoc === undefined) {
    pathBlockMatchers = [];
    return;
  }

  const webApiMapping = get(vpDoc, 'web_api_mapping', {});
  const blockedAPIs = get(vpDoc, 'web_apis', []);
  const responseMode = get(vpDoc, 'response_mode', {});

  pathBlockMatchers = Object.keys(webApiMapping).reduce((acc: string[], api: string) => {
    const mappedPath = webApiMapping[api];
    const isMappedPath = typeof mappedPath === 'string';
    const isApiBlocked = blockedAPIs.some((blockedAPI: string) => api.indexOf(blockedAPI) >= 0);
    const isFullPageMode = get(responseMode, `${api}.mode`) === VP_BLOCK_MODE.FULL_PAGE;

    if (isMappedPath && isApiBlocked && isFullPageMode) {
      acc.push(mappedPath);
    }

    return acc;
  }, []);
};

setLocalVPDoc(visitorPriortizationConfig);

export const setVPDoc = (vpDoc?: VP.Doc) => {
  setLocalVPDoc(vpDoc);
  vpDocSubscribers.forEach((cb) => cb(vpDoc));
};

export const subscribeVPDocUpdates = (cb: (vpDoc?: VP.Doc) => void) => {
  vpDocSubscribers.add(cb);
  return () => {
    vpDocSubscribers.delete(cb);
  };
};

const syncConfigFromCache = () => {
  if (__BROWSER__) {
    const cachedVPDocRaw = sessionStorage.getItem(VP_DOC_KEY);

    if (cachedVPDocRaw === lastCachedVPDocRaw) {
      return;
    }

    lastCachedVPDocRaw = cachedVPDocRaw;
    if (cachedVPDocRaw === null) {
      setLocalVPDoc(undefined);
      return;
    }

    const cachedVPDoc = getCachedVPDoc();
    if (cachedVPDoc !== undefined) {
      setLocalVPDoc(cachedVPDoc);
    }
  }
};

const { key: userSegment = 0 } = getUserSegment();
export const callOnBlockCallback = () => {
  // call on block end of the current stack execution
  setTimeout(() => {
    onBlockedCallback();
  }, 0);
};

const getEndpointResponse = (endpoint: string) => {
  let vpErrorMessage = get(
    visitorPriortizationConfig,
    'default_message',
    'Unfortunately, we are currently not serving. Hold tight, we will be back soon!'
  );
  let vpBlockMode = VP_BLOCK_MODE.RESPONSE;

  const apiResponseMode = get(visitorPriortizationConfig, 'response_mode');

  if (!apiResponseMode) {
    return { vpErrorMessage, vpBlockMode };
  }

  const matchedApiResponse = Object.keys(apiResponseMode).find(
    (api: string) => endpoint.indexOf(api) >= 0
  );

  if (!matchedApiResponse || !apiResponseMode[matchedApiResponse]) {
    return { vpErrorMessage, vpBlockMode };
  }

  vpBlockMode =
    apiResponseMode[matchedApiResponse].mode === VP_BLOCK_MODE.FULL_PAGE
      ? VP_BLOCK_MODE.FULL_PAGE
      : VP_BLOCK_MODE.RESPONSE;

  const responseMessage = get(apiResponseMode[matchedApiResponse], 'message');

  if (responseMessage) {
    vpErrorMessage = responseMessage;
  }

  return { vpErrorMessage, vpBlockMode };
};

/**
 * Check if API is blocked for current user as per visitor priortization
 * @param endpoint API URL
 */
export const isAPIBlocked = (endpoint: string) => {
  // Keep in-memory VP config in sync with session cache populated by initializeApp.
  syncConfigFromCache();

  const vpStatus = get(visitorPriortizationConfig, 'state', VP_STATE.ALLOW_ALL);
  if (
    visitorPriortizationConfig === undefined ||
    // visitor priortization disabled
    vpStatus === VP_STATE.ALLOW_ALL
  ) {
    return { isBlocked: false };
  }

  const blockPercentage = get(visitorPriortizationConfig, 'block_percentage', 0);

  // If user is not in blocked segment API request is allowed
  if (userSegment > blockPercentage) {
    return { isBlocked: false };
  }

  const { vpBlockMode, vpErrorMessage } = getEndpointResponse(endpoint);

  // if whole application blocked and user is in blocked segment, all API request is blocked
  if (vpStatus === VP_STATE.BLOCKED_ALL) {
    // show full page block only if added in full page block mode
    if (vpBlockMode === VP_BLOCK_MODE.FULL_PAGE) {
      callOnBlockCallback();
    }
    return { isBlocked: true, message: vpErrorMessage };
  }

  const blockedAPIs = get(visitorPriortizationConfig, 'web_apis', []);
  const apiMatched = blockedAPIs.some((api: string) => endpoint.indexOf(api) >= 0);

  // if api endpoint is not in blocked list do nothing
  if (apiMatched === false) {
    return { isBlocked: false };
  }

  // show full page block only if added in full page block mode
  if (vpBlockMode === VP_BLOCK_MODE.FULL_PAGE) {
    callOnBlockCallback();
  }
  // api request is blocked
  return { isBlocked: true, message: vpErrorMessage };
};

export const isPathBlocked = (pathname: string) => {
  syncConfigFromCache();

  const vpStatus = get(visitorPriortizationConfig, 'state', VP_STATE.ALLOW_ALL);
  if (
    visitorPriortizationConfig === undefined ||
    vpStatus === VP_STATE.ALLOW_ALL ||
    userSegment > get(visitorPriortizationConfig, 'block_percentage', 0)
  ) {
    return false;
  }

  if (vpStatus === VP_STATE.BLOCKED_ALL) {
    return true;
  }

  return pathBlockMatchers.some((mappedPath) => pathname.indexOf(mappedPath) >= 0);
};

export const setOnBlockCallback = (cb: VoidFunction) => {
  onBlockedCallback = cb;

  // clear function
  return () => {
    if (onBlockedCallback === cb) {
      onBlockedCallback = EMPTY_FUNC;
    }
  };
};

export const getVPDoc = () => visitorPriortizationConfig;
